import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrashAlt, 
  faPlus, 
  faEdit, 
  faSync, 
  faRocket,
  faFlask, 
  faBan,
  faCode,
  faList,
  faBars,
  faChevronDown,
  faChevronUp,
  faTrash,
  faCog,
  faSave,
  faTimes,
  faCheck,
  faIndustry,
  faVial,
  faTriangleExclamation
} from '@fortawesome/free-solid-svg-icons';
import AddFirmwareModal from './AddFirmwareModal';
import AllReleasesModal from './AllReleasesModal';
import ActiveTestReleaseLockModal, { LockedActiveTest } from './ActiveTestReleaseLockModal';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';


// Update interfaces to match database schema
interface BinFile {
  path: string;
  offset: number;
}

interface Release {
  _id: string;
  model: string;
  version: string;
  type: 'alpha' | 'dev' | 'beta' | 'prod' | 'depr' | 'factory';
  hardware?: string;
  clearState: boolean;
  enabled: boolean;
  internalOnly: boolean; // New field
  addToChangelog?: boolean;
  label?: string; // Optional short label for dev/alpha builds
  releaseNotes: string; // New field
  technicalDetails: string;
  targetMobileAppVersion: string; // Target mobile app version
  bins: BinFile[];
  createdBy: {
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface FirmwareFile {
  name: string;
  path: string;
  uploadedAt?: string;
}

interface Model {
  _id: string;
  name: string;
  code: string;
  description: string;
}

interface Hardware {
  _id: string;
  type: string;
  revision: number;
  binPath: string;
  enabled: boolean;
  description?: string;
  createdAt: string;
  updatedAt: string;
}

interface ReleaseEditorProps {
  firmwareFiles: FirmwareFile[];
  limitDisplay?: number;
  onModelChange?: (model: string | null) => void;
  onGenerateManifests?: () => Promise<void>;
  isGeneratingManifests?: boolean;
}

interface AllReleasesModalProps {
  show: boolean;
  onClose: () => void;
  releases: any[];
  model: string;
  onUpdateRelease: (model: string, index: number, field: string, value: any) => void;
  allReleases: any[];
}

// Add this after the imports
const styles = `
  .bg-purple {
    background-color: #6f42c1 !important;
    color: white !important;
  }
  .bg-factory {
    background-color: #6c757d !important;  /* Bootstrap's grey color */
    color: white !important;
  }
`;

// Hardware List Component
interface HardwareListProps {
  modelType: string;
}

const HardwareList: React.FC<HardwareListProps> = ({ modelType }) => {
  const [hardware, setHardware] = useState<Hardware[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  
  useEffect(() => {
    if (modelType) {
      fetchHardware();
    }
  }, [modelType]);

  const fetchHardware = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/hardware/type/${encodeURIComponent(modelType)}`, {
        withCredentials: true
      });
      setHardware(response.data.hardware || []);
    } catch (error) {
      console.error('Error fetching hardware:', error);
      setHardware([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 100 }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (hardware.length === 0) {
    return (
      <div className="alert alert-info">
        No hardware configurations found for {modelType}. Hardware configurations define which binary files are used for each hardware revision.
      </div>
    );
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm table-striped">
        <thead>
          <tr>
            <th>Revision</th>
            <th>Binary Path</th>
            <th>Description</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {hardware.map((hw) => (
            <tr key={hw._id}>
              <td>
                <span className="badge bg-secondary">Rev {hw.revision}</span>
              </td>
              <td>
                <code className="text-muted">{hw.binPath}</code>
              </td>
              <td>{hw.description || '-'}</td>
              <td>
                <span className={`badge ${hw.enabled ? 'bg-success' : 'bg-danger'}`}>
                  {hw.enabled ? 'Enabled' : 'Disabled'}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const PROMOTION_ORDER = ['dev', 'alpha', 'beta', 'prod'];
type DeploymentReleaseType = 'dev' | 'alpha' | 'beta' | 'prod';

const isDeploymentReleaseType = (type: Release['type']): type is DeploymentReleaseType =>
  PROMOTION_ORDER.includes(type);

const getDeploymentLabel = (type: DeploymentReleaseType) =>
  type === 'prod' ? 'production' : type;

const isPromotion = (oldType: Release['type'], newType: Release['type']) => {
  const oldIndex = PROMOTION_ORDER.indexOf(oldType);
  const newIndex = PROMOTION_ORDER.indexOf(newType);
  return oldIndex !== -1 && newIndex !== -1 && newIndex > oldIndex;
};

const ReleaseEditor: React.FC<ReleaseEditorProps> = ({
  firmwareFiles,
  limitDisplay = 6,
  onModelChange,
  onGenerateManifests,
  isGeneratingManifests = false
}) => {
  const [releases, setReleases] = useState<Release[]>([]);
  const [models, setModels] = useState<Model[]>([]);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  const [activeModel, setActiveModel] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [showAddFirmwareModal, setShowAddFirmwareModal] = useState<boolean>(false);
  const [showAllReleasesModal, setShowAllReleasesModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [showGenerateManifestsModal, setShowGenerateManifestsModal] = useState<boolean>(false);
  const [promotedRelease, setPromotedRelease] = useState<Release | null>(null);
  const [deploymentChangeRelease, setDeploymentChangeRelease] = useState<Release | null>(null);
  const [targetDeployment, setTargetDeployment] = useState<DeploymentReleaseType>('dev');
  const [generateManifestsOnDeploymentChange, setGenerateManifestsOnDeploymentChange] = useState<boolean>(true);
  const [deleteTarget, setDeleteTarget] = useState<{model: string, id: string, version: string, type: string} | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [typeLockAlert, setTypeLockAlert] = useState<string | null>(null);
  const [activeTestLock, setActiveTestLock] = useState<{
    message: string;
    tests: LockedActiveTest[];
    pending: {
      id: string;
      field: string;
      value: any;
      options?: { skipManifestPrompt?: boolean; generateManifests?: boolean };
    };
  } | null>(null);
  const [lockContinuing, setLockContinuing] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [showCreateReleaseModal, setShowCreateReleaseModal] = useState(false);
  const [releasesLoading, setReleasesLoading] = useState(false);
  const [syncingWiki, setSyncingWiki] = useState(false);
  const [syncWikiStatus, setSyncWikiStatus] = useState<{ type: 'success' | 'warning' | 'danger'; message: string } | null>(null);
  
  // Add this at the start of the component
  useEffect(() => {
    // Add the styles to the document
    const styleSheet = document.createElement("style");
    styleSheet.innerText = styles;
    document.head.appendChild(styleSheet);

    // Cleanup on unmount
    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);
  
  // Helper function to get badge color based on firmware type
  const getBadgeColorClass = (type: string): string => {
    switch (type) {
      case 'factory':
        return 'bg-warning text-dark';  // Bootstrap's yellow with dark text
      case 'prod':
        return 'bg-success';
      case 'beta':
        return 'bg-primary';
      case 'alpha':
        return 'bg-purple';
      case 'dev':
        return 'bg-danger';
      case 'depr':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };
  
  // Helper function to get icon based on firmware type
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'factory':
        return <FontAwesomeIcon icon={faIndustry} className="me-1" />;
      case 'prod':
        return <FontAwesomeIcon icon={faRocket} className="me-1" />;
      case 'beta':
        return <FontAwesomeIcon icon={faFlask} className="me-1" />;
      case 'alpha':
        return <FontAwesomeIcon icon={faVial} className="me-1" />;
      case 'dev':
        return <FontAwesomeIcon icon={faCode} className="me-1" />;
      case 'depr':
        return <FontAwesomeIcon icon={faBan} className="me-1" />;
      default:
        return null;
    }
  };
  
  // Add this function to fetch models
  const fetchModels = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/models`, {
        withCredentials: true
      });
      setModels(response.data.models);
      
      // Set the first model as active if we have models and no active model
      if (response.data.models.length > 0 && !activeModel) {
        const firstModel = response.data.models[0].name;
        setActiveModel(firstModel);
        onModelChange?.(firstModel);
      }
    } catch (error) {
      setError('Failed to fetch models');
    }
  };

  // Update useEffect to fetch models on mount
  useEffect(() => {
    fetchModels();
  }, []);

  // Update useEffect to fetch releases when activeModel changes
  useEffect(() => {
    if (activeModel) {
      fetchReleases(activeModel);
    }
  }, [activeModel]);

  const fetchReleases = async (model: string) => {
    setReleasesLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/releases/${model}`, {
        withCredentials: true
      });
      setReleases(response.data.releases);
      
      if (response.data.releases.length > 0) {
        const firstModel = response.data.releases[0].model;
        setExpandedSections({ [firstModel]: true });
        setActiveModel(firstModel);
      }
    } catch (error) {
      setError('Failed to fetch releases');
    } finally {
      setReleasesLoading(false);
    }
  };

  // Add a new release
  const addFirmwareEntry = async (model: string, version: string, type: string, internalOnly: boolean = false, releaseNotes: string = '', technicalDetails: string = '', clearState: boolean = true, targetMobileAppVersion: string = '', label: string = '') => {
    try {
      const modelObj = models.find(m => m.name === model);
      if (!modelObj) {
        setError('Model not found');
        return;
      }

      const newRelease = {
        model: model,
        version,
        type,
        clearState: clearState,
        enabled: true,
        internalOnly: internalOnly,
        label: label,
        releaseNotes: releaseNotes,
        technicalDetails: technicalDetails,
        targetMobileAppVersion: targetMobileAppVersion,
        bins: [
          {
            path: `https://firmware.fluoraplant.com/bins/bootloader.bin`,
            offset: 4096
          },
          {
            path: `https://firmware.fluoraplant.com/bins/partitions-v1.bin`,
            offset: 32768
          },
          {
            path: `https://firmware.fluoraplant.com/bins/boot_app0-v1.bin`,
            offset: 57344
          },
          {
            path: `https://firmware.fluoraplant.com/bins/${model.replace(/\s+/g, '_')}-v${version}.bin`,
            offset: 65536
          }
        ]
      };

      const response = await fetch(`${API_BASE_URL}/releases`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(newRelease),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const createdRelease = await response.json();
      await fetchReleases(model);
    } catch (error) {
      setError('Failed to create release');
      console.error('Error creating release:', error);
    }
  };

  // Update a release
  const handleUpdateRelease = async (
    id: string,
    field: string,
    value: string | boolean | Release | any,
    options: { skipManifestPrompt?: boolean; generateManifests?: boolean } = {}
  ) => {
    const previousRelease = releases.find(release => release._id === id);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/releases/${id}`,
        field === 'all' ? value : { [field]: value },
        { withCredentials: true }
      );
      setReleases(prev => 
        prev.map(release => 
          release._id === id ? response.data.release : release
        )
      );

      if (options.generateManifests && onGenerateManifests) {
        await onGenerateManifests();
      } else if (
        field === 'type' &&
        previousRelease &&
        typeof value === 'string' &&
        isPromotion(previousRelease.type, value as Release['type']) &&
        !options.skipManifestPrompt
      ) {
        setPromotedRelease(response.data.release);
        setShowGenerateManifestsModal(true);
      }
      return true;
    } catch (error) {
      const message =
        axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : 'Failed to update release';
      const lockedByTest = axios.isAxiosError(error) && error.response?.data?.code === 'ACTIVE_TEST_FIRMWARE_LOCK';
      const lockedTests = axios.isAxiosError(error) && Array.isArray(error.response?.data?.tests)
        ? error.response.data.tests as LockedActiveTest[]
        : [];
      if (lockedByTest && lockedTests.length) {
        setActiveTestLock({
          message,
          tests: lockedTests,
          pending: { id, field, value, options },
        });
        setDeploymentChangeRelease(null);
        return false;
      }
      if (
        lockedByTest ||
        field === 'type' ||
        (field === 'all' && previousRelease && typeof value === 'object' && value?.type !== previousRelease.type)
      ) {
        setTypeLockAlert(message);
        setDeploymentChangeRelease(null);
      }
      setError(message);
      return false;
    }
  };

  const continuePendingReleaseAction = async () => {
    if (!activeTestLock || lockContinuing) return;
    setLockContinuing(true);
    const { pending } = activeTestLock;
    const updated = await handleUpdateRelease(pending.id, pending.field, pending.value, pending.options || {});
    if (updated) {
      setActiveTestLock(null);
      if (pending.field === 'all') {
        setShowEditModal(false);
      }
      setDeploymentChangeRelease(null);
    }
    setLockContinuing(false);
  };

  const handleGenerateManifestsAfterPromotion = async () => {
    if (!onGenerateManifests) {
      setShowGenerateManifestsModal(false);
      setPromotedRelease(null);
      return;
    }

    try {
      await onGenerateManifests();
      setShowGenerateManifestsModal(false);
      setPromotedRelease(null);
    } catch (error) {
      // Error handling is done in the parent callback
    }
  };

  const handleSyncWiki = async () => {
    setSyncingWiki(true);
    setSyncWikiStatus(null);
    try {
      const response = await axios.post(
        `${API_BASE_URL}/releases/gitbook/sync`,
        {},
        { withCredentials: true }
      );
      setSyncWikiStatus({
        type: response.data?.warning ? 'warning' : 'success',
        message: response.data?.message || 'Synced GitBook release section.',
      });
    } catch (error) {
      setSyncWikiStatus({
        type: 'danger',
        message: axios.isAxiosError(error) && error.response?.data?.message
          ? String(error.response.data.message)
          : 'Failed to sync GitBook release section.',
      });
    } finally {
      setSyncingWiki(false);
    }
  };

  const openDeploymentChangeModal = (release: Release) => {
    const nextDeployment = PROMOTION_ORDER.find(type => type !== release.type) as DeploymentReleaseType | undefined;
    setDeploymentChangeRelease(release);
    setTargetDeployment(nextDeployment || 'dev');
    setGenerateManifestsOnDeploymentChange(Boolean(onGenerateManifests));
  };

  const handleDeploymentChange = async () => {
    if (!deploymentChangeRelease || targetDeployment === deploymentChangeRelease.type) return;

    const updated = await handleUpdateRelease(deploymentChangeRelease._id, 'type', targetDeployment, {
      skipManifestPrompt: true,
      generateManifests: generateManifestsOnDeploymentChange,
    });
    if (!updated) return;

    setDeploymentChangeRelease(null);
  };

  // Add a wrapper function for the AllReleasesModal
  const handleDeprecatedUpdate = (model: string, index: number, field: string, value: any) => {
    const release = getReleasesByModel(model)[index];
    if (release) {
      handleUpdateRelease(release._id, field, value);
    }
  };

  // Delete a release
  const deleteFirmwareEntry = async (id: string) => {
    const releaseToDelete = releases.find(release => release._id === id);
    if (releaseToDelete?.type === 'factory') {
      setError('Cannot delete factory releases');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/releases/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete release');
      }

      setReleases(prev => prev.filter(release => release._id !== id));
    } catch (error) {
      setError('Failed to delete release');
      console.error('Error deleting release:', error);
    }
  };

  // Helper functions to get releases by model
  const getReleasesByModel = (model: string) => {
    return releases.filter(release => release.model === model);
  };

  const getSortedReleases = (model: string) => {
    const modelReleases = getReleasesByModel(model);
    return modelReleases.sort((a, b) => {
      const typeOrder = getTypeSortOrder(a.type) - getTypeSortOrder(b.type);
      if (typeOrder !== 0) return typeOrder;
      return compareVersions(b.version, a.version);
    });
  };
  
  // Toggle expanded section
  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };
  
  // Set active model
  const setActiveModelAndExpand = (model: string) => {
    setActiveModel(model);
    setExpandedSections(prev => ({ ...prev, [model]: true }));
    onModelChange?.(model);
  };
  
  // Add this helper function to get the sort order for release types
  const getTypeSortOrder = (type: string): number => {
    switch (type) {
      case 'factory': return -1;
      case 'prod': return 0;
      case 'beta': return 1;
      case 'alpha': return 2;
      case 'dev': return 3;
      case 'depr': return 4;
      default: return 5;
    }
  };

  // Fix the compareVersions function to put highest versions at the top
  const compareVersions = (a: string, b: string): number => {
    const aParts = a.split('.').map(Number);
    const bParts = b.split('.').map(Number);
    
    for (let i = 0; i < Math.max(aParts.length, bParts.length); i++) {
      const aPart = aParts[i] || 0;
      const bPart = bParts[i] || 0;
      
      if (aPart !== bPart) {
        return bPart - aPart; // Changed back to bPart - aPart to put highest versions at the top
      }
    }
    return 0;
  };

  // Add this function to get limited releases for a model
  const getLimitedReleases = (model: string) => {
    const modelReleases = getSortedReleases(model)
      .filter(release => release.type !== 'depr');
    return modelReleases.slice(0, limitDisplay);
  };
  
  // Helper function to get the total count of ALL releases for a model (including deprecated)
  const getReleaseCount = (model: string): number => {
    return releases.filter(release => release.model === model).length;
  };

  const getDeprecatedReleases = (model: string) => {
    if (!releases) return [];
    return getSortedReleases(model).filter(release => release.type === 'depr');
  };

  // Add this function to handle the delete button click
  const handleDeleteClick = (model: string, id: string) => {
    if (!releases) return;
    const release = releases.find(release => release._id === id);
    setDeleteTarget({ 
      model, 
      id,
      version: release?.version || '',
      type: release?.type || 'dev'
    });
    setShowDeleteModal(true);
  };

  // First, let's add a function to get the color for a type
  const getTypeColor = (type: string): { bg: string, border: string, shadow: string } => {
    switch (type) {
      case 'factory':
        return {
          bg: '#ffc107',
          border: '#ffc107',
          shadow: 'rgba(255, 193, 7, 0.25)'
        };
      case 'prod':
        return {
          bg: '#198754',
          border: '#198754',
          shadow: 'rgba(25, 135, 84, 0.25)'
        };
      case 'beta':
        return {
          bg: '#0d6efd',
          border: '#0d6efd',
          shadow: 'rgba(13, 110, 253, 0.25)'
        };
      case 'alpha':
        return {
          bg: '#6f42c1',
          border: '#6f42c1',
          shadow: 'rgba(111, 66, 193, 0.25)'
        };
      case 'dev':
        return {
          bg: '#dc3545',
          border: '#dc3545',
          shadow: 'rgba(220, 53, 69, 0.25)'
        };
      case 'depr':
        return {
          bg: '#6c757d',
          border: '#6c757d',
          shadow: 'rgba(108, 117, 125, 0.25)'
        };
      default:
        return {
          bg: '#6c757d',
          border: '#6c757d',
          shadow: 'rgba(108, 117, 125, 0.25)'
        };
    }
  };

  // Update the handleCreateRelease function
  const handleCreateRelease = async () => {
    if (!activeModel) return;

    try {
      // Get the current releases for the model
      const currentReleases = getReleasesByModel(activeModel);
      
      // Find the highest version number
      const highestVersion = currentReleases.reduce((max, release) => {
        const version = parseInt(release.version);
        return version > max ? version : max;
      }, 0);

      // Create new release with next version number
      const newRelease: Release = {
        _id: '', // This will be set by the server
        model: activeModel,
        version: (highestVersion + 1).toString(),
        type: 'dev',
        enabled: true,
        clearState: false,
        internalOnly: false, // Default to false
        releaseNotes: '', // Add releaseNotes
        technicalDetails: '',
        targetMobileAppVersion: '', // Add targetMobileAppVersion
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        bins: [], // Add empty bins array
        createdBy: {
          _id: 'system',
          firstName: 'System',
          lastName: 'User',
          email: 'system@example.com'
        }
      };

      // Update the release in the database
      await handleUpdateRelease(activeModel, currentReleases.length.toString(), 'create');

      // Close the modal
      setShowCreateReleaseModal(false);
    } catch (error) {
      console.error('Error creating release:', error);
      // You might want to show an error message to the user here
    }
  };

  // Add this function to check if a model has a factory release
  const hasFactoryRelease = (model: string): boolean => {
    return releases.some(release => release.model === model && release.type === 'factory');
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Releases</h5>
          <div>
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={handleSyncWiki}
              disabled={syncingWiki}
            >
              <FontAwesomeIcon icon={faSync} className="me-1" />
              {syncingWiki ? 'Syncing…' : 'Sync Wiki'}
            </button>
            <button
              className="btn btn-sm btn-outline-primary"
              onClick={() => setShowAllReleasesModal(true)}
            >
              <FontAwesomeIcon icon={faBars} className="me-1" /> View All
            </button>
          </div>
        </div>
      </div>
      <div className="card-body">
        {syncWikiStatus && (
          <div className={`alert alert-${syncWikiStatus.type} alert-dismissible fade show py-2`} role="alert">
            {syncWikiStatus.message}
            <button
              type="button"
              className="btn-close"
              onClick={() => setSyncWikiStatus(null)}
              aria-label="Close"
            />
          </div>
        )}
        <div className="row">
          {/* Left sidebar with model list */}
          <div className="col-md-3">
            <div className="mb-2 fw-bold" style={{ fontSize: '1.1rem' }}>Models</div>
            <div className="list-group mb-3">
              {models.map(model => (
                <button
                  key={model._id}
                  className={`list-group-item list-group-item-action ${
                    activeModel === model.name ? 'active' : ''
                  }`}
                  onClick={() => setActiveModelAndExpand(model.name)}
                >
                  {model.name}
                </button>
              ))}
            </div>
          </div>

          {/* Main content area */}
          <div className="col-md-9">
            {activeModel ? (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h4 className="mb-0">{activeModel}</h4>
                  <div>
                    <button 
                      className="btn btn-sm btn-success me-2" 
                      onClick={() => setShowAddFirmwareModal(true)}
                    >
                      <FontAwesomeIcon icon={faPlus} className="me-1" /> Create Release
                    </button>
                  </div>
                </div>
                
                {releasesLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 100 }}>
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : getReleaseCount(activeModel) === 0 ? (
                  <div className="alert alert-info">
                    No firmware entries for this model. Add one using the button above.
                  </div>
                ) : (
                  <div className="accordion">
                    {getLimitedReleases(activeModel).map((release, index) => {
                      return (
                        <div className="card mb-3" key={index}>
                          <div className="card-header">
                            <div className="d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="form-check form-switch me-2" style={{ marginBottom: 0 }} data-bs-toggle="tooltip" data-bs-placement="top" title="Enable/Disable the release">
                                  <input
                                    className="form-check-input"
                                    type="checkbox"
                                    role="switch"
                                    checked={release.enabled ?? true}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      handleUpdateRelease(release._id, 'enabled', e.target.checked ? 'true' : 'false');
                                    }}
                                    style={{
                                      cursor: 'pointer',
                                      backgroundColor: release.enabled ? '#198754' : '#dc3545',
                                      borderColor: release.enabled ? '#198754' : '#dc3545',
                                      backgroundImage: release.enabled 
                                        ? `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e")`
                                        : `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='3' fill='%23fff'/%3e%3c/svg%3e")`,
                                      backgroundPosition: release.enabled ? 'right center' : 'left center'
                                    }}
                                  />
                                </div>
                                <span className={`badge ${getBadgeColorClass(release.type)} me-2`}>
                                  {getTypeIcon(release.type)}
                                  {release.type}
                                </span>
                                <span className="fw-bold me-2">v{release.version}</span>
                                {(release.type === 'dev' || release.type === 'alpha') && release.label && (
                                  <span className="badge me-2" style={{backgroundColor: '#e9ecef', color: '#495057'}} title="Build label">
                                    {release.label}
                                  </span>
                                )}
                                {release.targetMobileAppVersion && (
                                  <span className="badge me-2" style={{backgroundColor: '#e9ecef', color: '#495057'}} title={`Target Mobile App Version: ${release.targetMobileAppVersion}`}>
                                    📱 v{release.targetMobileAppVersion}
                                  </span>
                                )}
                                {release.internalOnly && (
                                  <span className="badge bg-warning text-dark me-2">
                                    (internal only)
                                  </span>
                                )}
                              </div>
                              <div className="d-flex align-items-center">
                                {isDeploymentReleaseType(release.type) && !release.internalOnly && (
                                  <button 
                                    className="btn btn-sm btn-outline-primary me-2"
                                    title="Change deployment"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      openDeploymentChangeModal(release);
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faRocket} className="me-1 fa-icon" /> Promote
                                  </button>
                                )}
                                {release.type === 'prod' && (
                                  <button 
                                    className="btn btn-sm btn-outline-danger me-2"
                                    title="Deprecate"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleUpdateRelease(release._id, 'type', 'depr');
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faBan} className="me-1 fa-icon" /> Deprecate
                                  </button>
                                )}
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => {
                                    setSelectedRelease(release);
                                    setShowEditModal(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faCog} className="me-1 fa-icon" /> Edit
                                </button>
                              </div>
                            </div>
                          </div>
                          
                          {expandedSections[`${activeModel}-${index}`] && (
                            <div className="card-body">
                              <div className="row mb-3">
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Version</label>
                                  <input 
                                    type="text" 
                                    className="form-control"
                                    value={release.version}
                                    onChange={(e) => handleUpdateRelease(release._id, 'version', e.target.value)}
                                  />
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Type</label>
                                  <select 
                                    className="form-select form-select-sm"
                                    value={release.type || 'depr'}
                                    onChange={(e) => {
                                      handleUpdateRelease(release._id, 'type', e.target.value);
                                      if (e.target.value !== 'depr') {
                                        handleUpdateRelease(release._id, 'enabled', true);
                                      }
                                    }}
                                  >
                                    {release.internalOnly ? (
                                      <>
                                        <option value="dev">Development</option>
                                        <option value="alpha">Alpha</option>
                                      </>
                                    ) : (
                                      <>
                                        <option value="depr">Deprecated</option>
                                        <option value="prod">Production</option>
                                        <option value="beta">Beta</option>
                                        <option value="alpha">Alpha</option>
                                        <option value="dev">Development</option>
                                      </>
                                    )}
                                  </select>
                                  {release.internalOnly && (
                                    <div className="form-text text-muted">
                                      Internal-only releases are restricted to "dev" or "alpha"
                                    </div>
                                  )}
                                </div>
                                <div className="col-md-4">
                                  <label className="form-label fw-bold">Options</label>
                                  <div>
                                    <div className="form-check form-check-inline">
                                      <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id={`clearState-${index}`}
                                        checked={release.clearState || false}
                                        onChange={(e) => handleUpdateRelease(release._id, 'clearState', e.target.checked ? 'true' : 'false')}
                                      />
                                      <label className="form-check-label" htmlFor={`clearState-${index}`}>
                                        Clear State
                                      </label>
                                    </div>
                                    <div className="form-check form-check-inline">
                                      <input 
                                        className="form-check-input" 
                                        type="checkbox" 
                                        id={`internalOnly-${index}`}
                                        checked={release.internalOnly || false}
                                        onChange={(e) => handleUpdateRelease(release._id, 'internalOnly', e.target.checked ? 'true' : 'false')}
                                      />
                                      <label className="form-check-label" htmlFor={`internalOnly-${index}`}>
                                        Internal Only
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              
                              <h6 className="mt-3 mb-2 fw-bold">
                                Binary Files
                                <button 
                                  className="btn btn-sm btn-outline-primary ms-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAddFirmwareModal(true);
                                  }}
                                >
                                  <FontAwesomeIcon icon={faPlus} className="me-1 fa-icon" /> Add Bin
                                </button>
                              </h6>
                              
                              {/* Release Notes Section */}
                              {release.releaseNotes && (
                                <div className="mt-3 mb-3">
                                  <h6 className="fw-bold">Release Notes</h6>
                                  <div className="card">
                                    <div className="card-body">
                                      <pre className="mb-0" style={{ 
                                        whiteSpace: 'pre-wrap', 
                                        fontFamily: 'inherit',
                                        fontSize: '0.875rem'
                                      }}>
                                        {release.releaseNotes}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {release.technicalDetails && (
                                <div className="mt-3 mb-3">
                                  <h6 className="fw-bold">Technical Details</h6>
                                  <div className="card">
                                    <div className="card-body">
                                      <pre className="mb-0" style={{
                                        whiteSpace: 'pre-wrap',
                                        fontFamily: 'inherit',
                                        fontSize: '0.875rem'
                                      }}>
                                        {release.technicalDetails}
                                      </pre>
                                    </div>
                                  </div>
                                </div>
                              )}

                              <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                  <thead className="table-light">
                                    <tr>
                                      <th style={{ width: "70%" }}>Path</th>
                                      <th style={{ width: "20%" }}>Offset</th>
                                      <th style={{ width: "10%" }}>Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {release.bins.map((bin, binIndex) => (
                                      <tr key={binIndex}>
                                        <td>
                                          <input 
                                            type="text" 
                                            className="form-control form-control-sm"
                                            value={bin.path}
                                            onChange={(e) => handleUpdateRelease(release._id, `bins.${binIndex}.path`, e.target.value)}
                                          />
                                        </td>
                                        <td>
                                          <input 
                                            type="number" 
                                            className="form-control form-control-sm"
                                            value={bin.offset}
                                            onChange={(e) => handleUpdateRelease(release._id, `bins.${binIndex}.offset`, e.target.value)}
                                          />
                                        </td>
                                        <td className="text-center">
                                          <button 
                                            className="btn btn-sm btn-outline-danger"
                                            onClick={(e) => handleDeleteClick(activeModel, release._id)}
                                          >
                                            <FontAwesomeIcon icon={faTrash} />
                                          </button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    <div className="text-center mt-3">
                      <small className="text-muted">
                        Showing {Math.min(limitDisplay, getLimitedReleases(activeModel).length)} of {getReleaseCount(activeModel)} releases
                      </small>
                    </div>
                  </div>
                )}
                
                {/* Hardware Section */}
                <div className="mt-4">
                  <h5 className="mb-3">Hardware Configurations</h5>
                  <HardwareList modelType={activeModel} />
                </div>
              </div>
            ) : (
              <div className="text-center py-5 text-muted">
                <p>Select a model from the list or create a new one</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Add Firmware Modal */}
      {activeModel && (
        <AddFirmwareModal
          isOpen={showAddFirmwareModal}
          onClose={() => setShowAddFirmwareModal(false)}
          onAdd={(version, type, internalOnly, releaseNotes, technicalDetails, clearState, targetMobileAppVersion, label) =>
            addFirmwareEntry(activeModel, version, type, internalOnly, releaseNotes, technicalDetails, clearState, targetMobileAppVersion, label)
          }
          firmwareFiles={firmwareFiles}
          model={activeModel}
          existingReleases={getReleasesByModel(activeModel).map(release => ({
            version: release.version,
            type: release.type
          }))}
        />
      )}

      {activeModel && (
        <AllReleasesModal
          show={showAllReleasesModal}
          onClose={() => setShowAllReleasesModal(false)}
          releases={getSortedReleases(activeModel)}
          model={activeModel}
          onUpdateRelease={handleDeprecatedUpdate}
          allReleases={getReleasesByModel(activeModel)}
        />
      )}

      <div
        className={`modal fade ${typeLockAlert ? 'show' : ''}`}
        style={{ display: typeLockAlert ? 'block' : 'none' }}
        tabIndex={-1}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Can't change release type</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setTypeLockAlert(null)}
              />
            </div>
            <div className="modal-body">
              <div className="alert alert-warning mb-0">
                <FontAwesomeIcon icon={faTriangleExclamation} className="me-2" />
                {typeLockAlert}
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setTypeLockAlert(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      </div>
      {typeLockAlert && <div className="modal-backdrop fade show" />}

      <ActiveTestReleaseLockModal
        show={Boolean(activeTestLock)}
        message={activeTestLock?.message || ''}
        tests={activeTestLock?.tests || []}
        continuing={lockContinuing}
        onClose={() => {
          if (lockContinuing) return;
          setActiveTestLock(null);
        }}
        onContinue={continuePendingReleaseAction}
        onTestsChange={(tests) => {
          setActiveTestLock((current) => current ? { ...current, tests } : current);
        }}
      />

      {/* Deployment Change Modal */}
      <div
        className={`modal fade ${deploymentChangeRelease ? 'show' : ''}`}
        style={{ display: deploymentChangeRelease ? 'block' : 'none' }}
        tabIndex={-1}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Change Deployment</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => setDeploymentChangeRelease(null)}
              />
            </div>
            <div className="modal-body">
              {deploymentChangeRelease && (
                <>
                  <div className="alert alert-info mb-3">
                    <div className="d-flex align-items-center flex-wrap gap-2">
                      <span className={`badge ${getBadgeColorClass(deploymentChangeRelease.type)}`}>
                        {getTypeIcon(deploymentChangeRelease.type)}
                        {deploymentChangeRelease.type}
                      </span>
                      <span className="fw-bold">{deploymentChangeRelease.model}</span>
                      <span className="text-muted">v{deploymentChangeRelease.version}</span>
                    </div>
                  </div>

                  <label htmlFor="target-deployment" className="form-label fw-bold">
                    Promote or demote to
                  </label>
                  <select
                    id="target-deployment"
                    className="form-select"
                    value={targetDeployment}
                    onChange={(e) => setTargetDeployment(e.target.value as DeploymentReleaseType)}
                  >
                    {PROMOTION_ORDER
                      .filter(type => type !== deploymentChangeRelease.type)
                      .map(type => (
                        <option key={type} value={type}>
                          {getDeploymentLabel(type as DeploymentReleaseType)}
                        </option>
                      ))}
                  </select>

                  <div className="form-check form-switch mt-3">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      role="switch"
                      id="generate-manifests-on-deployment-change"
                      checked={generateManifestsOnDeploymentChange}
                      onChange={(e) => setGenerateManifestsOnDeploymentChange(e.target.checked)}
                      disabled={!onGenerateManifests || isGeneratingManifests}
                    />
                    <label className="form-check-label" htmlFor="generate-manifests-on-deployment-change">
                      Generate Manifests
                    </label>
                    {!onGenerateManifests && (
                      <div className="form-text">Manifest generation is not available in this view.</div>
                    )}
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeploymentChangeRelease(null)}
                disabled={isGeneratingManifests}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleDeploymentChange}
                disabled={!deploymentChangeRelease || targetDeployment === deploymentChangeRelease.type || isGeneratingManifests}
              >
                {isGeneratingManifests ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Deployment
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {deploymentChangeRelease && (
        <div className="modal-backdrop fade show" />
      )}

      {/* Delete Confirmation Modal */}
      <div className={`modal fade ${showDeleteModal ? 'show' : ''}`} 
           style={{ display: showDeleteModal ? 'block' : 'none' }} 
           tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Confirm Delete</h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTarget(null);
                }}
              />
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this release?</p>
              {deleteTarget && (
                <>
                  <div className="alert alert-info mb-3">
                    <div className="d-flex align-items-center">
                      <span className={`badge ${getBadgeColorClass(deleteTarget.type)} me-2`}>
                        {getTypeIcon(deleteTarget.type)}
                        {deleteTarget.type}
                      </span>
                      <span className="fw-bold">v{deleteTarget.version}</span>
                    </div>
                  </div>
                  <div className="alert alert-warning">
                    <strong>Warning:</strong> This action cannot be undone.
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteTarget(null);
                }}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-danger" 
                onClick={async () => {
                  if (deleteTarget) {
                    await deleteFirmwareEntry(deleteTarget.id);
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      {showDeleteModal && (
        <div className="modal-backdrop fade show" />
      )}

      {/* Generate Manifests Prompt */}
      <div
        className={`modal fade ${showGenerateManifestsModal ? 'show' : ''}`}
        style={{ display: showGenerateManifestsModal ? 'block' : 'none' }}
        tabIndex={-1}
      >
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Generate Manifests?</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowGenerateManifestsModal(false);
                  setPromotedRelease(null);
                }}
                disabled={isGeneratingManifests}
              />
            </div>
            <div className="modal-body">
              {promotedRelease && (
                <>
                  <p className="mb-3">
                    Release promoted successfully. Would you like to regenerate web flasher manifests now?
                  </p>
                  <div className="alert alert-info mb-0">
                    <div className="d-flex align-items-center">
                      <span className={`badge ${getBadgeColorClass(promotedRelease.type)} me-2`}>
                        {getTypeIcon(promotedRelease.type)}
                        {promotedRelease.type}
                      </span>
                      <span className="fw-bold me-2">{promotedRelease.model}</span>
                      <span className="text-muted">v{promotedRelease.version}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowGenerateManifestsModal(false);
                  setPromotedRelease(null);
                }}
                disabled={isGeneratingManifests}
              >
                Not Now
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleGenerateManifestsAfterPromotion}
                disabled={isGeneratingManifests || !onGenerateManifests}
              >
                {isGeneratingManifests ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                    Generating...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSync} className="me-2" />
                    Generate Manifests
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
      {showGenerateManifestsModal && (
        <div className="modal-backdrop fade show" />
      )}

      {showEditModal && selectedRelease && (
        <>
          {/* Custom dark overlay */}
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100vw',
              height: '100vh',
              background: 'rgba(0,0,0,0.7)', // darker than Bootstrap's default
              zIndex: 1040
            }}
          />
          <div
            className="modal show d-block"
            tabIndex={-1}
            role="dialog"
            style={{ zIndex: 1050 }} // ensure modal is above overlay
          >
            <div className="modal-dialog modal-lg" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title fw-bold">
                   {selectedRelease.model} - v{selectedRelease.version}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowEditModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <div className="row">
                    {/* Left column: Model, Version, Type */}
                    <div className="col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold">Model</label>
                        <input
                          type="text"
                          className="form-control bg-light text-muted"
                          value={selectedRelease.model}
                          readOnly
                          style={{ cursor: 'not-allowed' }}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label fw-bold">Version</label>
                        <input
                          type="text"
                          className="form-control"
                          value={selectedRelease.version}
                          onChange={e => setSelectedRelease({ ...selectedRelease, version: e.target.value })}
                          readOnly={selectedRelease.type !== 'factory'}
                          style={{ cursor: selectedRelease.type === 'factory' ? 'text' : 'not-allowed' }}
                        />
                      </div>
                      <div className="mb-2">
                        <label className="form-label fw-bold">Type</label>
                        <select
                          className="form-select"
                          value={selectedRelease.type}
                          onChange={e => {
                            if (selectedRelease.type === 'factory') {
                              return;
                            }
                            setSelectedRelease({ ...selectedRelease, type: e.target.value as any });
                          }}
                          disabled={selectedRelease.type === 'factory'}
                        >
                          {selectedRelease.internalOnly ? (
                            <>
                              <option value="dev">Development</option>
                              <option value="alpha">Alpha</option>
                            </>
                          ) : (
                            <>
                              <option value="dev">Development</option>
                              <option value="alpha">Alpha</option>
                              <option value="beta">Beta</option>
                              <option value="prod">Production</option>
                              <option value="depr">Deprecated</option>
                              <option value="factory">Factory</option>
                            </>
                          )}
                        </select>
                        {selectedRelease.internalOnly && (
                          <div className="form-text text-muted">
                            Internal-only releases are restricted to "dev" or "alpha"
                          </div>
                        )}
                      </div>
                      {(selectedRelease.type === 'dev' || selectedRelease.type === 'alpha') && (
                        <div className="mb-2">
                          <label className="form-label fw-bold">Label</label>
                          <input
                            type="text"
                            className="form-control"
                            value={selectedRelease.label || ''}
                            onChange={e => setSelectedRelease({ ...selectedRelease, label: e.target.value })}
                            placeholder="e.g., hotfix, demo unit"
                          />
                          <div className="form-text">
                            Shown next to the version for dev/alpha builds. Hidden once promoted to beta or production.
                          </div>
                        </div>
                      )}
                      {/* Add hardware revision field for factory releases */}
                      {selectedRelease.type === 'factory' && (
                        <div className="mb-2">
                          <label className="form-label fw-bold">Hardware Revision</label>
                          <input
                            type="text"
                            className="form-control"
                            value={selectedRelease.hardware || ''}
                            onChange={e => setSelectedRelease({ ...selectedRelease, hardware: e.target.value })}
                            placeholder="e.g., 1, 2, 3"
                          />
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <label className="form-label fw-bold">Options</label>
                        <div>
                          <div className="form-check form-check-inline">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="edit-clearState"
                              checked={!!selectedRelease.clearState}
                              onChange={(e) => setSelectedRelease({ ...selectedRelease, clearState: e.target.checked })}
                            />
                            <label className="form-check-label" htmlFor="edit-clearState">
                              Clear State
                            </label>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Target Mobile App Version */}
                  <div className="mt-4 mb-3">
                    <label className="form-label fw-bold">Target Mobile App Version</label>
                    <input
                      type="text"
                      className="form-control"
                      value={selectedRelease.targetMobileAppVersion || ''}
                      onChange={e => setSelectedRelease({ ...selectedRelease, targetMobileAppVersion: e.target.value })}
                      placeholder="e.g., 2.1.0, 3.0.0"
                    />
                    <div className="form-text">
                      Specify the intended mobile app version for this firmware release.
                    </div>
                  </div>

                  {/* Release Notes Section */}
                  <div className="mt-4 mb-3">
                    <label className="form-label fw-bold">Release Notes (Markdown)</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={selectedRelease.releaseNotes || ''}
                      onChange={e => setSelectedRelease({ ...selectedRelease, releaseNotes: e.target.value })}
                      placeholder="Enter release notes in markdown format..."
                      style={{ fontFamily: 'monospace' }}
                    />
                    <div className="form-text">
                      Use markdown formatting for rich text. Examples: **bold**, *italic*, `code`, [links](url), etc.
                    </div>
                  </div>

                  {/* Technical Details Section */}
                  <div className="mt-4 mb-3">
                    <label className="form-label fw-bold">Technical Details (Markdown)</label>
                    <textarea
                      className="form-control"
                      rows={8}
                      value={selectedRelease.technicalDetails || ''}
                      onChange={e => setSelectedRelease({ ...selectedRelease, technicalDetails: e.target.value })}
                      placeholder="Enter detailed technical changes, fixes, known issues, checksums, migration notes, etc..."
                      style={{ fontFamily: 'monospace' }}
                    />
                    <div className="form-text">
                      More complete release information for staff and technical users. Public release pages hide this behind a dropdown.
                    </div>
                  </div>

                  {/* Editable Binaries */}
                  <div className="mt-4 mb-5">
                    <label className="form-label fw-bold mb-3">Binaries</label>
                    {selectedRelease.bins.map((bin, idx) => (
                      <div key={idx} className="d-flex align-items-center mb-2">
                        <input
                          type="text"
                          className="form-control me-2"
                          style={{ width: '60%' }}
                          value={bin.path}
                          onChange={e => {
                            const newBins = [...selectedRelease.bins];
                            newBins[idx] = { ...newBins[idx], path: e.target.value };
                            setSelectedRelease({ ...selectedRelease, bins: newBins });
                          }}
                          placeholder="Path"
                        />
                        <input
                          type="number"
                          className="form-control me-2"
                          style={{ width: '20%' }}
                          value={bin.offset}
                          onChange={e => {
                            const newBins = [...selectedRelease.bins];
                            newBins[idx] = { ...newBins[idx], offset: Number(e.target.value) };
                            setSelectedRelease({ ...selectedRelease, bins: newBins });
                          }}
                          placeholder="Offset"
                        />
                        <button
                          className="btn btn-outline-danger btn-sm"
                          onClick={() => {
                            const newBins = selectedRelease.bins.filter((_, i) => i !== idx);
                            setSelectedRelease({ ...selectedRelease, bins: newBins });
                          }}
                          title="Remove bin"
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                    <button
                      className="btn btn-outline-primary btn-sm mt-2"
                      onClick={() => {
                        setSelectedRelease({
                          ...selectedRelease,
                          bins: [
                            ...selectedRelease.bins,
                            { path: '', offset: 0 }
                          ]
                        });
                      }}
                    >
                      Add Bin
                    </button>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-danger me-auto"
                    onClick={() => {
                      setShowEditModal(false);
                      handleDeleteClick(selectedRelease.model, selectedRelease._id);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} className="me-2" />
                    Delete Release
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowEditModal(false)}
                  >
                    <FontAwesomeIcon icon={faTimes} className="me-2" />
                    Close
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={async () => {
                      const saved = await handleUpdateRelease(
                        selectedRelease._id,
                        'all',
                        selectedRelease
                      );
                      if (saved) {
                        setShowEditModal(false);
                      }
                    }}
                  >
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    Save Changes
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ReleaseEditor;